import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { toast } from "@/lib/toast";
import { Crosshair, Copy, Check, ShieldCheck, BadgeCheck, Users, Handshake, Loader2 } from "lucide-react";

// The funnel, in plain language. Deliberately NO points number anywhere member-facing (Phase 1) —
// the four stages ARE the reward story: reach → joined → verified → completed a deal.
const STAGES = [
  { key: "clicks", label: "Reach", icon: Users, hint: "Opened your link" },
  { key: "joined", label: "Joined", icon: Handshake, hint: "Created an account" },
  { key: "verified", label: "Verified", icon: BadgeCheck, hint: "Passed ID check" },
  { key: "released", label: "Completed a deal", icon: ShieldCheck, hint: "Bought or sold on the marketplace" },
];

function inviteeStage(inv) {
  if (inv.released_at) return { label: "Completed a deal", color: "#8FA84A" };
  if (inv.verified_at) return { label: "Verified", color: "#8FB8D8" };
  return { label: "Pending verification", color: "#8E9195" };
}

export default function Invite() {
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/invites/me").then(({ data }) => setData(data)).catch(() => setData({ error: true }));
  }, [user]);

  if (loading || user === null) return <div className="min-h-screen" />;
  if (!user) return <Navigate to="/login" replace />;

  // Build the share link from the live origin so it is always correct regardless of API host.
  const code = data?.code || "";
  const link = code ? `${window.location.origin}/i/${code}` : "";
  const stats = data?.stats || { clicks: 0, joined: 0, verified: 0, released: 0 };
  const invitees = data?.invitees || [];

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Invite link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy — select and copy the link manually.");
    }
  };

  return (
    <div className="min-h-screen text-foreground" data-testid="invite-page">
      <Navbar />
      <div className="max-w-3xl mx-auto px-5 py-10">
        {/* Lead with the link + the why — the common case is zero invites, so we never open on four zeros. */}
        <div className="border border-sniper bg-surface p-6 sm:p-8" data-testid="invite-hero">
          <div className="flex items-center gap-2 text-olive-bright">
            <Crosshair className="w-5 h-5" strokeWidth={1.6} />
            <span className="font-mono text-[11px] uppercase tracking-[0.14em]">Your personal invite</span>
          </div>
          <h1 className="mt-4 font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight" data-testid="invite-title">
            Invite someone you'd vouch for
          </h1>
          <p className="mt-3 text-sm font-body text-secondary-foreground max-w-[60ch]">
            OpticSwap is verified-only — every member is ID-verified before any money moves. When you invite someone,
            your handle vouches for them on their profile until they've done their first deal. Only invite people you'd stand behind.
          </p>

          <div className="mt-6 grid sm:grid-cols-[1fr_auto] gap-5 items-center">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Your link</label>
              <div className="mt-1.5 flex items-stretch border border-sniper bg-obsidian">
                <span className="flex-1 min-w-0 px-3 py-2.5 font-mono text-sm text-foreground truncate" data-testid="invite-link">
                  {link || (data?.error ? "Couldn't load your link" : "Loading…")}
                </span>
                <button onClick={copy} disabled={!link} data-testid="invite-copy-btn"
                  className="shrink-0 inline-flex items-center gap-1.5 border-l border-sniper bg-olive hover:bg-olive-bright disabled:opacity-40 text-white font-mono uppercase text-[11px] tracking-wide px-4 transition-colors">
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? "Copied" : "Copy"}
                </button>
              </div>
              {code && <p className="mt-2 font-mono text-[11px] uppercase tracking-wide text-muted-foreground" data-testid="invite-code">Code: <span className="text-olive-bright">{code}</span></p>}
            </div>
            {link && (
              <div className="justify-self-center sm:justify-self-end bg-white p-2.5" data-testid="invite-qr" title="Scan to open your invite link">
                <QRCodeSVG value={link} size={112} bgColor="#ffffff" fgColor="#0b0e07" level="M" includeMargin={false} />
              </div>
            )}
          </div>
        </div>

        {/* The funnel — four plain stages, never a points number. */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="invite-funnel">
          {STAGES.map(({ key, label, icon: Icon, hint }) => (
            <div key={key} className="border border-sniper bg-surface p-4" data-testid={`invite-stat-${key}`}>
              <Icon className="w-4 h-4 text-olive-bright" strokeWidth={1.7} />
              <p className="mt-2 font-heading text-3xl font-bold tabular-nums" data-testid={`invite-stat-${key}-value`}>{stats[key] ?? 0}</p>
              <p className="font-mono text-[10px] uppercase tracking-wide text-foreground mt-0.5">{label}</p>
              <p className="text-[11px] font-body text-muted-foreground mt-0.5">{hint}</p>
            </div>
          ))}
        </div>
        <p className="mt-2 text-[11px] font-body text-muted-foreground" data-testid="invite-count-note">
          "Invited members" counts <span className="text-[#8FB8D8]">Verified</span> only — a signup that never passes ID isn't counted as a member.
        </p>

        {/* Invitee list — empty state stays quiet and points back at the link. */}
        <div className="mt-6 border border-sniper bg-surface" data-testid="invite-list">
          <div className="px-4 py-3 border-b border-sniper flex items-center gap-2">
            <Users className="w-4 h-4 text-olive-bright" />
            <span className="font-mono text-[11px] uppercase tracking-wide">People you've invited</span>
          </div>
          {!data ? (
            <div className="p-6 flex items-center gap-2 text-muted-foreground font-mono text-xs uppercase" data-testid="invite-list-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : invitees.length === 0 ? (
            <div className="p-8 text-center" data-testid="invite-list-empty">
              <p className="text-sm font-body text-secondary-foreground">No one yet.</p>
              <p className="text-[12.5px] font-body text-muted-foreground mt-1">Share your link above with someone you'd vouch for — they'll show up here once they join.</p>
            </div>
          ) : (
            <div className="divide-y divide-sniper">
              {invitees.map((inv, i) => {
                const st = inviteeStage(inv);
                return (
                  <div key={i} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`invite-invitee-${inv.username || i}`}>
                    <span className="font-body text-sm text-foreground truncate">@{inv.username || "member"}</span>
                    <span className="shrink-0 font-mono text-[10px] uppercase tracking-wide" style={{ color: st.color }} data-testid={`invite-invitee-stage-${inv.username || i}`}>{st.label}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
